import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  FaArrowLeft,
  FaBriefcase,
  FaChalkboardTeacher,
  FaClock,
  FaExclamationTriangle,
  FaPlay,
  FaSearch,
  FaSearchPlus,
  FaSpinner,
  FaSync,
  FaTachometerAlt,
  FaTimes,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import useApp from "../../hooks/useAppContext";
import api from "../../services/axios";
import "../../components/agenda/detalhesParticipantes.css";
import "./admPatrocinadoresList.css";
import "./admEntregas.css";

const POLLING_MS = 5000;

const FILTROS = [
  { id: "todas", label: "Todas" },
  { id: "iniciada", label: "Iniciadas" },
  { id: "nao_iniciada", label: "Não iniciadas" },
  { id: "adiantada", label: "Adiantadas" },
  { id: "atrasada", label: "Atrasadas" },
];

const LABELS_SITUACAO = {
  iniciada: "Iniciada",
  nao_iniciada: "Não iniciada",
  adiantada: "Adiantada",
  atrasada: "Atrasada",
};

/** Atrasadas → pendentes → entregues (iniciada/adiantada) */
const ORDEM_SITUACAO = {
  atrasada: 0,
  nao_iniciada: 1,
  iniciada: 2,
  adiantada: 2,
};

/** Reuniões de negócio primeiro, depois M&M */
const ORDEM_TIPO = {
  reuniao_negocio: 0,
  m_m: 1,
};

const LABELS_TIPO_REUNIAO = {
  reuniao_negocio: "Reuniões de negócio",
  m_m: "Match & Meet",
};

const LABELS_TIPO_CONTEUDO = {
  conteudo_patrocinado: "Conteúdo patrocinado",
  conteudo_editorial: "Conteúdo editorial",
};

const MODOS_MONITOR = [
  { id: "reunioes", label: "Reuniões" },
  { id: "conteudos", label: "Salas de conteúdo" },
];

function ordemTipo(tipo) {
  return ORDEM_TIPO[tipo] ?? 99;
}

function ConvidadoComAvatar({ convidado, handleImagemParticipante }) {
  const [zoomAberto, setZoomAberto] = useState(false);
  const src = handleImagemParticipante(convidado);
  const meta = [convidado?.cargo, convidado?.empresa].filter(Boolean).join(" / ");

  useEffect(() => {
    if (!zoomAberto) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") setZoomAberto(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [zoomAberto]);

  if (!convidado?.nome && !convidado?.id) {
    return "—";
  }

  return (
    <>
      <div className="entregas-convidado-cell">
        <button
          type="button"
          className="entregas-convidado-avatar-btn"
          onClick={() => setZoomAberto(true)}
          title="Ampliar foto"
          aria-label={`Ampliar foto de ${convidado?.nome || "convidado"}`}
        >
          <img
            src={src}
            alt=""
            className="apl-avatar-thumb entregas-convidado-avatar"
          />
          <span className="entregas-convidado-avatar-hint" aria-hidden>
            <FaSearchPlus size={10} />
          </span>
        </button>
        <div className="entregas-convidado-dados">
          <span className="entregas-convidado-nome">
            {convidado?.nome || "—"}
          </span>
          {convidado?.empresa ? (
            <div className="entregas-sub">{convidado.empresa}</div>
          ) : null}
        </div>
      </div>

      {zoomAberto
        ? createPortal(
            <div
              className="dp-zoom-overlay"
              role="dialog"
              aria-modal="true"
              aria-label={`Foto ampliada de ${convidado?.nome || "convidado"}`}
              onClick={() => setZoomAberto(false)}
            >
              <button
                type="button"
                className="dp-zoom-close"
                onClick={() => setZoomAberto(false)}
                aria-label="Fechar zoom"
              >
                <FaTimes size={16} />
              </button>
              <figure
                className="dp-zoom-figure"
                onClick={(event) => event.stopPropagation()}
              >
                <img src={src} alt={convidado?.nome || "Convidado"} />
                <figcaption>
                  <strong>{convidado?.nome || "—"}</strong>
                  {meta ? <span>{meta}</span> : null}
                </figcaption>
              </figure>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function normalizarHora(valor) {
  if (!valor) return "";
  const texto = String(valor);
  return texto.length >= 5 ? texto.substring(0, 5) : texto;
}

function labelHorario(valor) {
  const hora = normalizarHora(valor);
  if (!hora) return "—";
  return hora.replace(":", "H");
}

function formatarDesvio(minutos) {
  if (minutos === null || minutos === undefined) return "—";
  if (minutos === 0) return "no horário";
  const abs = Math.abs(minutos);
  const texto = abs === 1 ? "1 min" : `${abs} min`;
  return minutos < 0 ? `${texto} adiantada` : `${texto} atrasada`;
}

function formatarInicioReuniao(valor) {
  if (!valor) return "—";
  const texto = String(valor);
  let parsed = DateTime.fromFormat(texto.substring(0, 19), "yyyy-MM-dd HH:mm:ss");
  if (!parsed.isValid) {
    parsed = DateTime.fromISO(texto);
  }
  if (!parsed.isValid) return texto;
  return parsed.setLocale("pt-BR").toFormat("ccc dd/MM HH:mm");
}

function formatarSaidaAgenda(valor) {
  if (!valor) return "—";
  const texto = String(valor);
  let parsed = DateTime.fromFormat(texto.substring(0, 19), "yyyy-MM-dd HH:mm:ss");
  if (!parsed.isValid) {
    parsed = DateTime.fromISO(texto);
  }
  if (!parsed.isValid) return texto;
  return parsed.toFormat("dd/MM HH:mm");
}

function itemSaiu(item) {
  return Boolean(item?.saiu || item?.saida_agenda || item?.saidaAgenda);
}

function scrollChipCompleto(container, chip) {
  if (!container || !chip) return;
  const cRect = container.getBoundingClientRect();
  const eRect = chip.getBoundingClientRect();
  const margem = 8;

  if (eRect.left < cRect.left + margem) {
    container.scrollBy({
      left: eRect.left - cRect.left - margem,
      behavior: "smooth",
    });
    return;
  }

  if (eRect.right > cRect.right - margem) {
    container.scrollBy({
      left: eRect.right - cRect.right + margem,
      behavior: "smooth",
    });
  }
}

function parseSlotDateTime(dataIso, horaValor) {
  const hora = normalizarHora(horaValor);
  if (!dataIso || !hora) return null;
  const parsed = DateTime.fromFormat(`${dataIso} ${hora}`, "yyyy-MM-dd HH:mm");
  return parsed.isValid ? parsed : null;
}

function enriquecerSlotsDoDia(slots, dataIso) {
  const ordenados = (slots || [])
    .filter((s) => s.data === dataIso)
    .slice()
    .sort((a, b) =>
      String(a.horario_inicio || "").localeCompare(String(b.horario_inicio || ""))
    );

  return ordenados
    .map((slot, index) => {
      const inicio = parseSlotDateTime(slot.data, slot.horario_inicio);
      if (!inicio) return null;

      let fim = parseSlotDateTime(slot.data, slot.horario_fim);
      if (!fim || fim <= inicio) {
        const proximo = ordenados[index + 1];
        const inicioProximo = proximo
          ? parseSlotDateTime(proximo.data, proximo.horario_inicio)
          : null;
        fim =
          inicioProximo && inicioProximo > inicio
            ? inicioProximo
            : inicio.plus({ minutes: 30 });
      }

      return { ...slot, inicio, fim };
    })
    .filter(Boolean);
}

/** Slot em andamento agora; senão o mais recente já iniciado; senão o primeiro do dia. */
function resolverSlotPadraoDoDia(slots, dataIso, agora = DateTime.now()) {
  const slotsDia = enriquecerSlotsDoDia(slots, dataIso);
  if (!slotsDia.length) return null;

  const acontecendo = slotsDia.find(
    (slot) => agora >= slot.inicio && agora < slot.fim
  );
  if (acontecendo) return acontecendo;

  const jaIniciados = slotsDia.filter((slot) => agora >= slot.inicio);
  if (jaIniciados.length > 0) {
    return jaIniciados[jaIniciados.length - 1];
  }

  return slotsDia[0];
}

/** Dia do evento vigente para o momento atual + slot padrão desse dia. */
function resolverFiltroPadrao(slots, diasComSlots, agora = DateTime.now()) {
  const datas = (diasComSlots || []).map((d) => d.data).filter(Boolean).sort();
  if (!datas.length) {
    return { data: "", slotId: null };
  }

  const hoje = agora.toISODate();
  let dataAlvo = datas[0];

  if (datas.includes(hoje)) {
    dataAlvo = hoje;
  } else if (hoje > datas[datas.length - 1]) {
    dataAlvo = datas[datas.length - 1];
  } else if (hoje < datas[0]) {
    dataAlvo = datas[0];
  } else {
    dataAlvo = datas.find((data) => data >= hoje) || datas[datas.length - 1];
  }

  const slot = resolverSlotPadraoDoDia(slots, dataAlvo, agora);
  return {
    data: dataAlvo,
    slotId: slot?.id ?? null,
  };
}

const AdmMonitoramentoEntregas = () => {
  const navigate = useNavigate();
  const { handleImagemParticipante } = useApp();

  const [diasEvento, setDiasEvento] = useState([]);
  const [slots, setSlots] = useState([]);
  const [dataSelecionada, setDataSelecionada] = useState("");
  const [slotIdFiltro, setSlotIdFiltro] = useState(null);
  const [filtroSituacao, setFiltroSituacao] = useState("todas");
  const [modoMonitor, setModoMonitor] = useState("reunioes");
  const [busca, setBusca] = useState("");

  const [reunioes, setReunioes] = useState([]);
  const [conteudos, setConteudos] = useState([]);
  const [contadores, setContadores] = useState({
    total: 0,
    iniciadas: 0,
    nao_iniciadas: 0,
    adiantadas: 0,
    atrasadas: 0,
  });
  const [agoraServidor, setAgoraServidor] = useState("");
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const [isLoadingDados, setIsLoadingDados] = useState(false);
  const [error, setError] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  const dataRef = useRef("");
  const filtroInicialAplicadoRef = useRef(false);
  const slotsChipsRef = useRef(null);
  const situacaoChipsRef = useRef(null);

  const modoConteudos = modoMonitor === "conteudos";

  useEffect(() => {
    dataRef.current = dataSelecionada;
  }, [dataSelecionada]);

  const carregarMeta = useCallback(async () => {
    setIsLoadingMeta(true);
    setError(null);
    try {
      const [respDias, respSlots] = await Promise.all([
        api.get("/agenda-dias-evento"),
        api.get("/slots"),
      ]);

      const dias = respDias.data?.dados?.dias || [];
      const listaSlots = respSlots.data?.dados || [];
      setSlots(listaSlots);

      const datasSlots = [
        ...new Set(listaSlots.map((s) => s.data).filter(Boolean)),
      ].sort();

      const diasBase =
        dias.length > 0
          ? dias
          : datasSlots.map((data) => {
              const parsed = DateTime.fromISO(data);
              return {
                data,
                dia: data.substring(8, 10),
                label: parsed.isValid ? parsed.toFormat("dd/MM") : data,
              };
            });

      // Só abas de dias que possuem slots cadastrados
      const diasComSlots = diasBase.filter((item) =>
        datasSlots.includes(item.data)
      );
      const diasResolvidos =
        diasComSlots.length > 0
          ? diasComSlots
          : datasSlots.map((data) => {
              const parsed = DateTime.fromISO(data);
              return {
                data,
                dia: data.substring(8, 10),
                label: parsed.isValid ? parsed.toFormat("dd/MM") : data,
              };
            });

      setDiasEvento(diasResolvidos);

      const padrao = resolverFiltroPadrao(
        listaSlots,
        diasResolvidos,
        DateTime.now()
      );

      if (!filtroInicialAplicadoRef.current) {
        setDataSelecionada(padrao.data);
        setSlotIdFiltro(padrao.slotId);
        filtroInicialAplicadoRef.current = true;
      } else {
        setDataSelecionada((atual) => {
          if (atual && diasResolvidos.some((d) => d.data === atual)) {
            return atual;
          }
          return padrao.data;
        });
      }
    } catch (err) {
      console.error("Erro ao carregar meta do monitoramento:", err);
      setError("Erro ao carregar dias e slots do evento ativo.");
    } finally {
      setIsLoadingMeta(false);
    }
  }, []);

  const carregarMonitoramento = useCallback(async (opts = {}) => {
    const data = opts.data ?? dataRef.current;
    const silencioso = Boolean(opts.silencioso);

    if (!data) return;

    if (!silencioso) setIsLoadingDados(true);
    if (!silencioso) setError(null);

    try {
      const resp = await api.get("/reunioes-monitoramento-entregas", {
        params: { data },
      });
      const dados = resp.data?.dados || {};

      setReunioes(dados.reunioes || []);
      setConteudos(dados.conteudos || []);
      setContadores(
        dados.contadores || {
          total: 0,
          iniciadas: 0,
          nao_iniciadas: 0,
          adiantadas: 0,
          atrasadas: 0,
        }
      );
      setAgoraServidor(dados.agora || "");
      setUltimaAtualizacao(DateTime.local());
    } catch (err) {
      console.error("Erro ao carregar monitoramento:", err);
      if (!silencioso) {
        setError("Erro ao carregar o monitoramento de entregas.");
      }
    } finally {
      if (!silencioso) setIsLoadingDados(false);
    }
  }, []);

  useEffect(() => {
    carregarMeta();
  }, [carregarMeta]);

  useEffect(() => {
    if (!dataSelecionada || isLoadingMeta) return undefined;

    carregarMonitoramento({ data: dataSelecionada });

    const timer = setInterval(() => {
      carregarMonitoramento({
        data: dataRef.current,
        silencioso: true,
      });
    }, POLLING_MS);

    return () => clearInterval(timer);
  }, [dataSelecionada, isLoadingMeta, carregarMonitoramento]);

  const slotsDoDia = useMemo(() => {
    if (!dataSelecionada) return [];
    return slots
      .filter((s) => s.data === dataSelecionada)
      .slice()
      .sort((a, b) =>
        String(a.horario_inicio || "").localeCompare(
          String(b.horario_inicio || "")
        )
      );
  }, [slots, dataSelecionada]);

  useEffect(() => {
    if (!slotsDoDia.length) {
      if (slotIdFiltro !== null) setSlotIdFiltro(null);
      return;
    }
    const slotAindaValido = slotsDoDia.some((s) => s.id === slotIdFiltro);
    if (slotAindaValido) return;

    const slotPadrao = resolverSlotPadraoDoDia(
      slots,
      dataSelecionada,
      DateTime.now()
    );
    setSlotIdFiltro(slotPadrao?.id ?? slotsDoDia[0].id);
  }, [slotsDoDia, slotIdFiltro, slots, dataSelecionada]);

  useEffect(() => {
    const ativo = slotsChipsRef.current?.querySelector(".is-active");
    if (!ativo) return;
    requestAnimationFrame(() => {
      scrollChipCompleto(slotsChipsRef.current, ativo);
    });
  }, [slotIdFiltro, dataSelecionada, slotsDoDia]);

  useEffect(() => {
    const ativo = situacaoChipsRef.current?.querySelector(".is-active");
    if (!ativo) return;
    requestAnimationFrame(() => {
      scrollChipCompleto(situacaoChipsRef.current, ativo);
    });
  }, [filtroSituacao]);

  const horarioSlotFiltro = useMemo(() => {
    if (!slotIdFiltro) return null;
    const slot = slotsDoDia.find((s) => s.id === slotIdFiltro);
    return slot ? normalizarHora(slot.horario_inicio) : null;
  }, [slotIdFiltro, slotsDoDia]);

  const itensBase = modoConteudos ? conteudos : reunioes;

  const itensDoSlot = useMemo(() => {
    if (!horarioSlotFiltro) return [];
    return itensBase.filter((r) => r.horario === horarioSlotFiltro);
  }, [itensBase, horarioSlotFiltro]);

  const contadoresVisiveis = useMemo(() => {
    return {
      total: itensDoSlot.length,
      iniciadas: itensDoSlot.filter(
        (r) => r.situacao === "iniciada" || r.situacao === "adiantada"
      ).length,
      nao_iniciadas: itensDoSlot.filter((r) => r.situacao === "nao_iniciada")
        .length,
      adiantadas: itensDoSlot.filter((r) => r.situacao === "adiantada")
        .length,
      atrasadas: itensDoSlot.filter((r) => r.situacao === "atrasada").length,
      sairam: itensDoSlot.filter((r) => itemSaiu(r)).length,
    };
  }, [itensDoSlot]);

  const diaAtivoLabel = useMemo(() => {
    const encontrado = diasEvento.find((d) => d.data === dataSelecionada);
    if (encontrado?.label) return encontrado.label;
    if (!dataSelecionada) return "—";
    const parsed = DateTime.fromISO(dataSelecionada);
    return parsed.isValid ? parsed.toFormat("dd/MM") : dataSelecionada;
  }, [diasEvento, dataSelecionada]);

  const itensFiltrados = useMemo(() => {
    let lista = itensDoSlot;

    if (filtroSituacao !== "todas") {
      if (filtroSituacao === "iniciada") {
        lista = lista.filter(
          (r) => r.situacao === "iniciada" || r.situacao === "adiantada"
        );
      } else {
        lista = lista.filter((r) => r.situacao === filtroSituacao);
      }
    }

    if (busca.trim()) {
      const termo = busca.toLowerCase().trim();
      lista = lista.filter((r) => {
        const campos = [
          r.convidado?.nome,
          r.convidado?.empresa,
          r.patrocinador?.empresa,
          r.patrocinador?.nome,
          r.nome,
          r.local,
          r.horario,
          r.tipo,
          LABELS_SITUACAO[r.situacao],
        ];
        return campos.some((c) =>
          String(c || "")
            .toLowerCase()
            .includes(termo)
        );
      });
    }

    return lista.slice().sort((a, b) => {
      if (modoConteudos) {
        const salaA = String(a.nome || "");
        const salaB = String(b.nome || "");
        const cmpSala = salaA.localeCompare(salaB, "pt-BR");
        if (cmpSala !== 0) return cmpSala;
      } else {
        const tipoA = ordemTipo(a.tipo);
        const tipoB = ordemTipo(b.tipo);
        if (tipoA !== tipoB) return tipoA - tipoB;
      }

      const ordemA = ORDEM_SITUACAO[a.situacao] ?? 99;
      const ordemB = ORDEM_SITUACAO[b.situacao] ?? 99;
      if (ordemA !== ordemB) return ordemA - ordemB;

      const nomeA = String(a.convidado?.nome || "");
      const nomeB = String(b.convidado?.nome || "");
      return nomeA.localeCompare(nomeB, "pt-BR");
    });
  }, [itensDoSlot, filtroSituacao, busca, modoConteudos]);

  const itensAgrupados = useMemo(() => {
    if (modoConteudos) {
      const mapa = new Map();
      for (const item of itensFiltrados) {
        const chave = `${item.id_agenda || item.nome}-${item.nome || ""}`;
        if (!mapa.has(chave)) {
          mapa.set(chave, {
            tipo: item.tipo,
            label: item.nome || "Sala de conteúdo",
            local: item.local || "",
            items: [],
          });
        }
        mapa.get(chave).items.push(item);
      }
      return Array.from(mapa.values());
    }

    const grupos = [
      { tipo: "reuniao_negocio", label: LABELS_TIPO_REUNIAO.reuniao_negocio, items: [] },
      { tipo: "m_m", label: LABELS_TIPO_REUNIAO.m_m, items: [] },
    ];

    for (const item of itensFiltrados) {
      const grupo = item.tipo === "m_m" ? grupos[1] : grupos[0];
      grupo.items.push(item);
    }

    return grupos.filter((grupo) => grupo.items.length > 0);
  }, [itensFiltrados, modoConteudos]);

  const selecionarDia = (data) => {
    setDataSelecionada(data);
    const slot = resolverSlotPadraoDoDia(slots, data, DateTime.now());
    setSlotIdFiltro(slot?.id ?? null);
  };

  return (
    <div className="container-adm-main">
      <div className="apl-page">
        <header className="apl-header">
          <div className="apl-header-text">
            <h1>Monitor de entregas</h1>
            <p className="entregas-monitor-subtitle">
              {modoConteudos
                ? "Acompanhe em tempo real a presença e a saída dos convidados nas salas de conteúdo."
                : "Acompanhe em tempo real quais reuniões já iniciaram, quais ainda não começaram, as adiantadas e as atrasadas para iniciar."}
            </p>
          </div>

          <div className="apl-toolbar apl-toolbar--inline">
            <div className="apl-search">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder={
                  modoConteudos
                    ? "Buscar convidado, sala ou horário…"
                    : "Buscar convidado, empresa ou horário…"
                }
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                aria-label="Buscar no monitor de entregas"
              />
            </div>

            <div className="apl-toolbar-actions">
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
                title="Voltar"
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </div>
        </header>

        {error ? (
          <div className="apl-alert" role="alert">
            {error}
          </div>
        ) : null}

        {isLoadingMeta ? (
          <div className="apl-card">
            <div className="apl-loading">
              <FaSpinner className="fa-spin" size={28} aria-hidden />
              <span>Carregando dias do evento…</span>
            </div>
          </div>
        ) : (
          <>
            <div className="entregas-monitor-daybar">
              {diasEvento.length > 0 ? (
                <nav className="apl-day-tabs" aria-label="Dias do evento">
                  {diasEvento.map((item) => (
                    <button
                      key={item.data}
                      type="button"
                      className={`apl-day-tab${
                        dataSelecionada === item.data ? " is-active" : ""
                      }`}
                      onClick={() => selecionarDia(item.data)}
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>
              ) : (
                <div />
              )}

              <div className="entregas-monitor-daybar-actions">
                <Link
                  to="/private/adm/reunioes"
                  className="apl-btn apl-btn--secondary entregas-monitor-slot-link"
                  title="Entregas por slot"
                >
                  <FaBriefcase size={14} aria-hidden />
                  <span className="entregas-monitor-slot-link-label">
                    Entregas por slot
                  </span>
                </Link>
                <button
                  type="button"
                  className="apl-btn apl-btn--ghost entregas-monitor-refresh"
                  onClick={() =>
                    carregarMonitoramento({
                      data: dataSelecionada,
                    })
                  }
                  disabled={isLoadingDados || isLoadingMeta}
                  title="Atualizar agora"
                >
                  <FaSync size={14} aria-hidden />
                  <span className="entregas-monitor-refresh-label">
                    Atualizar
                  </span>
                </button>
              </div>
            </div>

            <div
              className="entregas-monitor-modos"
              role="group"
              aria-label="Tipo de monitoramento"
            >
              {MODOS_MONITOR.map((modo) => (
                <button
                  key={modo.id}
                  type="button"
                  className={`entregas-monitor-modo-btn${
                    modoMonitor === modo.id ? " is-active" : ""
                  }`}
                  onClick={() => setModoMonitor(modo.id)}
                >
                  {modo.id === "conteudos" ? (
                    <FaChalkboardTeacher size={14} aria-hidden />
                  ) : (
                    <FaBriefcase size={14} aria-hidden />
                  )}
                  {modo.label}
                </button>
              ))}
            </div>

            <section
              className="apl-metrics entregas-monitor-metrics"
              aria-label="Indicadores ao vivo"
            >
              <button
                type="button"
                className={`apl-metric apl-metric--total${
                  filtroSituacao === "todas" ? " is-active" : ""
                }`}
                onClick={() => setFiltroSituacao("todas")}
              >
                <span className="apl-metric-value">{contadoresVisiveis.total}</span>
                <span className="apl-metric-label">Total no slot</span>
              </button>
              <button
                type="button"
                className={`apl-metric entregas-metric--ok${
                  filtroSituacao === "iniciada" ? " is-active" : ""
                }`}
                onClick={() => setFiltroSituacao("iniciada")}
              >
                <span className="apl-metric-value">{contadoresVisiveis.iniciadas}</span>
                <span className="apl-metric-label">Iniciadas</span>
              </button>
              <button
                type="button"
                className={`apl-metric${
                  filtroSituacao === "nao_iniciada" ? " is-active" : ""
                }`}
                onClick={() => setFiltroSituacao("nao_iniciada")}
              >
                <span className="apl-metric-value">
                  {contadoresVisiveis.nao_iniciadas}
                </span>
                <span className="apl-metric-label">Não iniciadas</span>
              </button>
              <button
                type="button"
                className={`apl-metric entregas-metric--early${
                  filtroSituacao === "adiantada" ? " is-active" : ""
                }`}
                onClick={() => setFiltroSituacao("adiantada")}
              >
                <span className="apl-metric-value">{contadoresVisiveis.adiantadas}</span>
                <span className="apl-metric-label">Adiantadas</span>
              </button>
              <button
                type="button"
                className={`apl-metric apl-metric--alert${
                  filtroSituacao === "atrasada" ? " is-active" : ""
                }`}
                onClick={() => setFiltroSituacao("atrasada")}
              >
                <span className="apl-metric-value">{contadoresVisiveis.atrasadas}</span>
                <span className="apl-metric-label">Atrasadas</span>
              </button>
              {modoConteudos ? (
                <div className="apl-metric entregas-metric--saidas" aria-live="polite">
                  <span className="apl-metric-value">{contadoresVisiveis.sairam}</span>
                  <span className="apl-metric-label">Saíram</span>
                </div>
              ) : null}
            </section>

            <div className="entregas-monitor-meta">
              <span>
                <FaTachometerAlt aria-hidden /> Dia {diaAtivoLabel}
              </span>
              <span>
                <FaClock aria-hidden /> Relógio servidor:{" "}
                {agoraServidor
                  ? agoraServidor.substring(11, 16)
                  : "—"}
              </span>
              <span>
                Atualizado:{" "}
                {ultimaAtualizacao
                  ? ultimaAtualizacao.toFormat("HH:mm:ss")
                  : "—"}{" "}
                (a cada 5s)
              </span>
            </div>

            {slotsDoDia.length > 0 ? (
              <div
                ref={slotsChipsRef}
                className="entregas-slot-chips"
                role="group"
                aria-label="Filtrar por slot"
              >
                {slotsDoDia.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    className={`entregas-slot-chip${
                      slotIdFiltro === slot.id ? " is-active" : ""
                    }`}
                    onClick={() => setSlotIdFiltro(slot.id)}
                  >
                    {labelHorario(slot.horario_inicio)}
                  </button>
                ))}
              </div>
            ) : null}

            <div
              ref={situacaoChipsRef}
              className="entregas-filtro-situacao"
              role="group"
              aria-label="Filtrar situação"
            >
              {FILTROS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`entregas-filtro-btn${
                    filtroSituacao === f.id ? " is-active" : ""
                  }`}
                  onClick={() => setFiltroSituacao(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <section
              className="apl-card"
              aria-label={
                modoConteudos
                  ? "Lista de salas de conteúdo"
                  : "Lista de reuniões"
              }
            >
              {isLoadingDados && itensBase.length === 0 ? (
                <div className="apl-loading">
                  <FaSpinner className="fa-spin" size={28} aria-hidden />
                  <span>Carregando monitoramento…</span>
                </div>
              ) : itensFiltrados.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">
                    {modoConteudos
                      ? "Nenhum convidado de conteúdo neste filtro"
                      : "Nenhuma reunião neste filtro"}
                  </p>
                  <p style={{ margin: 0 }}>
                    Ajuste o dia, horário ou situação para ver resultados.
                  </p>
                </div>
              ) : (
                <>
                  <div className="apl-table-wrap entregas-monitor-table">
                    <table className="apl-table">
                      <thead>
                        <tr>
                          <th scope="col">Horário</th>
                          <th scope="col">Convidado</th>
                          <th scope="col">
                            {modoConteudos ? "Sala / Conteúdo" : "Patrocinador"}
                          </th>
                          <th scope="col">Situação</th>
                          <th scope="col">Desvio</th>
                          <th scope="col">
                            {modoConteudos ? "Saída" : "Iniciou em"}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {itensAgrupados.map((grupo) => (
                          <React.Fragment
                            key={`${grupo.tipo}-${grupo.label}`}
                          >
                            <tr className="entregas-grupo-tipo">
                              <th colSpan={6} scope="colgroup">
                                <span className="entregas-grupo-tipo-label">
                                  {modoConteudos ? (
                                    <FaChalkboardTeacher size={12} aria-hidden />
                                  ) : grupo.tipo === "m_m" ? (
                                    <FaTachometerAlt size={12} aria-hidden />
                                  ) : (
                                    <FaBriefcase size={12} aria-hidden />
                                  )}
                                  {grupo.label}
                                  {modoConteudos && grupo.local ? (
                                    <span className="entregas-grupo-tipo-local">
                                      {grupo.local}
                                    </span>
                                  ) : null}
                                </span>
                                <span className="entregas-grupo-tipo-count">
                                  {grupo.items.length}
                                </span>
                              </th>
                            </tr>
                            {grupo.items.map((item) => {
                              const saiu = itemSaiu(item);
                              return (
                                <tr
                                  key={item.id}
                                  className={`entregas-row entregas-row--${item.situacao}`}
                                >
                                  <td>
                                    <span className="apl-stat">
                                      {item.horario || "—"}
                                    </span>
                                    {item.tipo === "m_m" ? (
                                      <span className="apl-tag-type apl-tag-type--mm">
                                        M&M
                                      </span>
                                    ) : null}
                                  </td>
                                  <td className="apl-cell-name">
                                    <ConvidadoComAvatar
                                      convidado={item.convidado}
                                      handleImagemParticipante={
                                        handleImagemParticipante
                                      }
                                    />
                                  </td>
                                  <td>
                                    {modoConteudos
                                      ? item.nome ||
                                        LABELS_TIPO_CONTEUDO[item.tipo] ||
                                        "—"
                                      : item.patrocinador?.empresa ||
                                        item.patrocinador?.nome ||
                                        "—"}
                                  </td>
                                  <td>
                                    <span
                                      className={`entregas-status entregas-status--${item.situacao}`}
                                    >
                                      {item.situacao === "iniciada" && !saiu ? (
                                        <FaPlay size={10} aria-hidden />
                                      ) : null}
                                      {item.situacao === "atrasada" ? (
                                        <FaExclamationTriangle
                                          size={10}
                                          aria-hidden
                                        />
                                      ) : null}
                                      {saiu
                                        ? "Saiu"
                                        : LABELS_SITUACAO[item.situacao] ||
                                          item.situacao}
                                    </span>
                                  </td>
                                  <td>{formatarDesvio(item.minutos_desvio)}</td>
                                  <td>
                                    {modoConteudos
                                      ? formatarSaidaAgenda(
                                          item.saida_agenda || item.saidaAgenda
                                        )
                                      : formatarInicioReuniao(item.iniciada_em)}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="entregas-monitor-cards"
                    aria-label={
                      modoConteudos
                        ? "Salas de conteúdo em cards"
                        : "Reuniões em cards"
                    }
                  >
                    {itensAgrupados.map((grupo) => (
                      <div
                        key={`${grupo.tipo}-${grupo.label}`}
                        className="entregas-monitor-cards-grupo"
                      >
                        <h3 className="entregas-grupo-tipo-heading">
                          <span className="entregas-grupo-tipo-label">
                            {modoConteudos ? (
                              <FaChalkboardTeacher size={12} aria-hidden />
                            ) : grupo.tipo === "m_m" ? (
                              <FaTachometerAlt size={12} aria-hidden />
                            ) : (
                              <FaBriefcase size={12} aria-hidden />
                            )}
                            {grupo.label}
                            {modoConteudos && grupo.local ? (
                              <span className="entregas-grupo-tipo-local">
                                {grupo.local}
                              </span>
                            ) : null}
                          </span>
                          <span className="entregas-grupo-tipo-count">
                            {grupo.items.length}
                          </span>
                        </h3>
                        {grupo.items.map((item) => {
                          const saiu = itemSaiu(item);
                          return (
                            <article
                              key={item.id}
                              className={`entregas-monitor-card entregas-monitor-card--${item.situacao}`}
                            >
                              <header className="entregas-monitor-card-head">
                                <div className="entregas-monitor-card-hora">
                                  <span className="apl-stat">
                                    {item.horario || "—"}
                                  </span>
                                  {item.tipo === "m_m" ? (
                                    <span className="apl-tag-type apl-tag-type--mm">
                                      M&M
                                    </span>
                                  ) : null}
                                </div>
                                <span
                                  className={`entregas-status entregas-status--${item.situacao}`}
                                >
                                  {item.situacao === "iniciada" && !saiu ? (
                                    <FaPlay size={10} aria-hidden />
                                  ) : null}
                                  {item.situacao === "atrasada" ? (
                                    <FaExclamationTriangle
                                      size={10}
                                      aria-hidden
                                    />
                                  ) : null}
                                  {saiu
                                    ? "Saiu"
                                    : LABELS_SITUACAO[item.situacao] ||
                                      item.situacao}
                                </span>
                              </header>

                              <div className="entregas-monitor-card-body">
                                <div className="entregas-monitor-card-field">
                                  <span className="entregas-monitor-card-label">
                                    Convidado
                                  </span>
                                  <ConvidadoComAvatar
                                    convidado={item.convidado}
                                    handleImagemParticipante={
                                      handleImagemParticipante
                                    }
                                  />
                                </div>

                                <div className="entregas-monitor-card-field">
                                  <span className="entregas-monitor-card-label">
                                    {modoConteudos
                                      ? "Sala / Conteúdo"
                                      : "Patrocinador"}
                                  </span>
                                  <span>
                                    {modoConteudos
                                      ? item.nome ||
                                        LABELS_TIPO_CONTEUDO[item.tipo] ||
                                        "—"
                                      : item.patrocinador?.empresa ||
                                        item.patrocinador?.nome ||
                                        "—"}
                                  </span>
                                </div>

                                <div className="entregas-monitor-card-meta">
                                  <div className="entregas-monitor-card-field">
                                    <span className="entregas-monitor-card-label">
                                      Desvio
                                    </span>
                                    <span>
                                      {formatarDesvio(item.minutos_desvio)}
                                    </span>
                                  </div>
                                  <div className="entregas-monitor-card-field">
                                    <span className="entregas-monitor-card-label">
                                      {modoConteudos ? "Saída" : "Iniciou em"}
                                    </span>
                                    <span>
                                      {modoConteudos
                                        ? formatarSaidaAgenda(
                                            item.saida_agenda ||
                                              item.saidaAgenda
                                          )
                                        : formatarInicioReuniao(
                                            item.iniciada_em
                                          )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdmMonitoramentoEntregas;
